import React, { memo } from 'react';
import { ListChecks, Undo2, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * The only trace a task operation leaves in the message stream.
 *
 * Deliberately slim: the panel is where tasks live, so this is just a way back
 * into a past turn's changeset. Reads like a commit stat line.
 *
 * Renders for turns that wrote nothing too. Those are the ones that most look
 * like a bug when they're silent: the reply says the board is already in that
 * state, and without a chip there is nothing to click to check.
 */
function TaskChangesChipImpl({ summary, total = 0, active = 0, noop = 0, skipped = 0, onOpen, conflicted = false }) {
    const created = summary?.created ?? 0;
    const updated = summary?.updated ?? 0;
    const deleted = summary?.deleted ?? 0;

    const wrote = created + updated + deleted > 0 || total > 0;
    if (!wrote && noop + skipped === 0) return null;

    const allReverted = wrote && active === 0 && total > 0;
    // Nothing was written, so there is nothing to undo and nothing to conflict.
    const quiet = !wrote;

    const stats = [
        { key: 'created', n: created, label: 'created', color: 'text-emerald-400' },
        { key: 'updated', n: updated, label: 'updated', color: 'text-primary' },
        { key: 'deleted', n: deleted, label: 'deleted', color: 'text-red-400' },
    ].filter((s) => s.n > 0);

    return (
        <button
            type="button"
            data-testid="task-changes-chip"
            onClick={onOpen}
            className={cn(
                'group/chip mt-3 flex w-full items-center gap-2.5 rounded-xl border px-3 py-2 text-left',
                'transition-colors duration-200',
                allReverted || quiet
                    ? 'border-white/10 bg-white/[0.02] hover:border-white/20'
                    : 'border-primary/25 bg-primary/[0.07] hover:border-primary/50 hover:bg-primary/[0.12]',
            )}
        >
            <span
                className={cn(
                    'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border',
                    allReverted || quiet
                        ? 'border-white/10 bg-white/5 text-muted-foreground'
                        : 'border-primary/30 bg-primary/15 text-primary',
                )}
            >
                {allReverted ? <Undo2 className="h-3.5 w-3.5" />
                    : quiet ? <CheckCircle2 className="h-3.5 w-3.5" />
                        : <ListChecks className="h-3.5 w-3.5" />}
            </span>

            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                {quiet ? (
                    <span className="font-medium text-muted-foreground">
                        {noop > 0 ? 'Already up to date' : null}
                        {noop > 0 && skipped > 0 ? ' · ' : null}
                        {skipped > 0 ? `${skipped} not matched` : null}
                    </span>
                ) : allReverted ? (
                    <span className="font-medium text-muted-foreground">
                        {total} change{total === 1 ? '' : 's'} reverted
                    </span>
                ) : (
                    stats.map((s, i) => (
                        <React.Fragment key={s.key}>
                            {i > 0 ? <span className="text-muted-foreground/40">·</span> : null}
                            <span className="font-medium">
                                <span className={cn('font-mono font-bold tabular-nums', s.color)}>{s.n}</span>
                                <span className="ml-1 text-muted-foreground">{s.label}</span>
                            </span>
                        </React.Fragment>
                    ))
                )}

                {conflicted ? (
                    <span className="rounded border border-amber-500/30 bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-400">
                        edited since
                    </span>
                ) : null}
            </span>

            <span className="flex flex-shrink-0 items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-[0.14em] text-muted-foreground transition-colors group-hover/chip:text-foreground">
                View
                <ArrowUpRight className="h-3 w-3 transition-transform duration-200 group-hover/chip:-translate-y-px group-hover/chip:translate-x-px" />
            </span>
        </button>
    );
}

export const TaskChangesChip = memo(TaskChangesChipImpl);
export default TaskChangesChip;
